// Cloudinary configuration and upload service
const CLOUDINARY_CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'your-cloud-name';
const CLOUDINARY_UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || 'your-upload-preset';
const CLOUDINARY_API_KEY = import.meta.env.VITE_CLOUDINARY_API_KEY || 'your-api-key';

class CloudinaryService {
  constructor() {
    this.cloudName = CLOUDINARY_CLOUD_NAME;
    this.uploadPreset = CLOUDINARY_UPLOAD_PRESET;
    this.apiKey = CLOUDINARY_API_KEY;
    this.baseUrl = `https://api.cloudinary.com/v1_1/${this.cloudName}`;
  }

  /**
   * Upload a single file to Cloudinary
   * @param {File} file - The file to upload
   * @param {Object} options - Upload options
   * @returns {Promise<Object>} Upload result with secure_url
   */
  async uploadFile(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', this.uploadPreset);
      
      // Add optional parameters
      if (options.folder) {
        formData.append('folder', options.folder);
      }
      
      if (options.resourceType) {
        formData.append('resource_type', options.resourceType);
      } else {
        // Auto-detect resource type based on file type
        if (file.type.startsWith('image/')) {
          formData.append('resource_type', 'image');
        } else if (file.type.startsWith('video/')) {
          formData.append('resource_type', 'video');
        } else {
          formData.append('resource_type', 'raw'); // For PDFs, documents, etc.
        }
      }
      
      if (options.publicId) {
        formData.append('public_id', options.publicId);
      }
      
      // Add tags for better organization
      if (options.tags) {
        formData.append('tags', Array.isArray(options.tags) ? options.tags.join(',') : options.tags);
      }

      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const result = await response.json();
      return {
        success: true,
        url: result.secure_url,
        publicId: result.public_id,
        resourceType: result.resource_type,
        format: result.format,
        bytes: result.bytes,
        width: result.width,
        height: result.height,
        originalFilename: result.original_filename,
      };
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Upload multiple files to Cloudinary
   * @param {File[]} files - Array of files to upload
   * @param {Object} options - Upload options
   * @returns {Promise<Object[]>} Array of upload results
   */
  async uploadMultipleFiles(files, options = {}) {
    try {
      const uploadPromises = files.map((file, index) => {
        const fileOptions = {
          ...options,
          publicId: options.publicId ? `${options.publicId}_${index}` : undefined,
        };
        return this.uploadFile(file, fileOptions);
      });

      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Multiple file upload error:', error);
      throw error;
    }
  }

  /**
   * Upload book icon/cover image
   * @param {File} file - Image file
   * @param {string} bookName - Book name for folder organization
   * @returns {Promise<Object>} Upload result
   */
  async uploadBookIcon(file, bookName) {
    const sanitizedBookName = bookName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    return this.uploadFile(file, {
      folder: `vaidic/books/${sanitizedBookName}/icons`,
      resourceType: 'image',
      tags: ['book-icon', 'e-library'],
    });
  }

  /**
   * Upload book PDF or document
   * @param {File} file - PDF/document file
   * @param {string} bookName - Book name for folder organization
   * @param {string} type - 'book' or 'chapter'
   * @returns {Promise<Object>} Upload result
   */
  async uploadBookDocument(file, bookName, type = 'book') {
    const sanitizedBookName = bookName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const folder = type === 'chapter' 
      ? `vaidic/books/${sanitizedBookName}/chapters`
      : `vaidic/books/${sanitizedBookName}/documents`;
    
    return this.uploadFile(file, {
      folder,
      resourceType: 'raw',
      tags: [type, 'e-library', 'document'],
    });
  }

  /**
   * Upload chapter document
   * @param {File} file - Chapter file
   * @param {string} bookName - Book name
   * @param {string} chapterName - Chapter name
   * @returns {Promise<Object>} Upload result
   */
  async uploadChapterDocument(file, bookName, chapterName) {
    const sanitizedBookName = bookName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const sanitizedChapterName = chapterName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    
    return this.uploadFile(file, {
      folder: `vaidic/books/${sanitizedBookName}/chapters`,
      publicId: `${sanitizedBookName}_${sanitizedChapterName}`,
      resourceType: 'raw',
      tags: ['chapter', 'e-library', 'document'],
    });
  }

  /**
   * Delete a file from Cloudinary
   * @param {string} publicId - Public ID of the file to delete
   * @param {string} resourceType - Type of resource ('image', 'video', 'raw')
   * @returns {Promise<Object>} Deletion result
   */
  async deleteFile(publicId, resourceType = 'image') {
    try {
      // Note: For production, you should implement server-side deletion
      // as it requires API secret which shouldn't be exposed in frontend
      console.warn('File deletion should be implemented on the server side for security');
      return {
        success: false,
        error: 'Deletion should be handled server-side',
      };
    } catch (error) {
      console.error('Cloudinary deletion error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Generate optimized URL for images
   * @param {string} publicId - Public ID of the image
   * @param {Object} transformations - Cloudinary transformations
   * @returns {string} Optimized image URL
   */
  generateOptimizedUrl(publicId, transformations = {}) {
    const baseUrl = `https://res.cloudinary.com/${this.cloudName}/image/upload`;
    
    const transformParams = [];
    
    if (transformations.width) transformParams.push(`w_${transformations.width}`);
    if (transformations.height) transformParams.push(`h_${transformations.height}`);
    if (transformations.crop) transformParams.push(`c_${transformations.crop}`);
    if (transformations.quality) transformParams.push(`q_${transformations.quality}`);
    if (transformations.format) transformParams.push(`f_${transformations.format}`);
    
    const transformString = transformParams.length > 0 ? `${transformParams.join(',')}/` : '';
    
    return `${baseUrl}/${transformString}${publicId}`;
  }

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @param {Object} constraints - Validation constraints
   * @returns {Object} Validation result
   */
  validateFile(file, constraints = {}) {
    const errors = [];
    
    // Check file size (default 50MB for documents, 5MB for images)
    const maxSize = constraints.maxSize || (file.type.startsWith('image/') ? 5 * 1024 * 1024 : 50 * 1024 * 1024);
    if (file.size > maxSize) {
      errors.push(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
    }
    
    // Check file type
    if (constraints.allowedTypes) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!constraints.allowedTypes.includes(fileExtension)) {
        errors.push(`File type ${fileExtension} is not allowed. Allowed types: ${constraints.allowedTypes.join(', ')}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export default new CloudinaryService();
